import { createSlice, createAsyncThunk, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { oauth } from "../utils";
import { toast } from "react-toastify";
import type {
  WorkflowAutomationId,
  WorkflowAutomationListItem,
  WorkflowAutomationDetail,
  WorkflowAutomationSaveInput,
  WorkflowAutomationModelType,
} from "../types/workflow_automation";
import {
  normalizeConfigurationOptions,
  type WorkflowConfigurationOptions,
  type WorkflowRawConfigurationOptions,
} from "../components/workflow-automations/catalog";

type LoadingState = "idle" | "pending";

export interface WorkflowAutomationsState {
  automations: WorkflowAutomationListItem[];
  currentAutomation: WorkflowAutomationDetail | null;
  /**
   * Editable copy of `currentAutomation` that the editor page reads/writes.
   * Reset to match `currentAutomation` whenever the server snapshot changes
   * (fetch, successful save, or explicit discard); left untouched on save failure
   * so in-progress edits aren't lost.
   */
  draftAutomation: WorkflowAutomationDetail | null;
  configurationOptions: WorkflowConfigurationOptions | null;
  listLoading: LoadingState;
  detailLoading: LoadingState;
  saveLoading: LoadingState;
  configurationOptionsLoading: LoadingState;
  /** workflow_id of the row whose active state is being toggled from the list, if any. */
  togglingWorkflowId: WorkflowAutomationId | null;
  error: string | null;
}

export type WorkflowAutomationsRootState = {
  workflowAutomations: WorkflowAutomationsState;
};
type RootState = WorkflowAutomationsRootState;

const initialState: WorkflowAutomationsState = {
  automations: [],
  currentAutomation: null,
  draftAutomation: null,
  configurationOptions: null,
  listLoading: "idle",
  detailLoading: "idle",
  saveLoading: "idle",
  configurationOptionsLoading: "idle",
  togglingWorkflowId: null,
  error: null,
};

/**
 * Thin typed wrapper around `oauth()`
 */
const oauthJson = async <T>(
  method: string,
  url: string,
  data: Record<string, unknown> = {}
): Promise<T> => {
  const { json } = await oauth(method, url, data);
  return json;
};

/**
 * Builds the payload expected by the create/update endpoints from a full workflow
 * detail record, optionally overriding the `active` flag (used by list-row toggling,
 * which only intends to flip that one field).
 */
export const toWorkflowAutomationSaveInput = (
  workflow: WorkflowAutomationDetail,
  overrides: Partial<WorkflowAutomationSaveInput> = {},
): WorkflowAutomationSaveInput => ({
  workflow_id: workflow.workflow_id,
  name: workflow.name,
  description: workflow.description,
  model_type: workflow.model_type,
  active: workflow.active,
  trigger: workflow.trigger ?? null,
  conditions: workflow.conditions,
  tasks: workflow.tasks,
  ...overrides,
});

const parseWorkflowErrorMessage = (e: any, fallback: string): string => {
  const errors = e?.json?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors[0]?.message || String(errors[0]);
  }
  if (typeof e?.json?.message === "string") {
    return e.json.message;
  }
  return fallback;
};

/** Merges a saved/toggled workflow detail back into its matching list row, if present. */
const patchAutomationListRow = (
  state: WorkflowAutomationsState,
  detail: WorkflowAutomationDetail
) => {
  const idx = state.automations.findIndex(
    (a) => a.workflow_id === detail.workflow_id
  );
  if (idx !== -1) {
    state.automations[idx] = {
      ...state.automations[idx],
      name: detail.name,
      model_type: detail.model_type,
      active: detail.active,
      last_triggered_at: detail.last_triggered_at,
      updated_by: detail.updated_by,
      n8n_provisioned: detail.n8n_provisioned,
    };
  }
};

export const FETCH_AUTOMATIONS = "workflowAutomations/fetchAutomations";
export const fetchAutomations = createAsyncThunk(FETCH_AUTOMATIONS, async () => {
  const { workflows } = await oauthJson<{
    workflows: WorkflowAutomationListItem[];
  }>("GET", "workflow");
  return workflows;
});

export const FETCH_AUTOMATION = "workflowAutomations/fetchAutomation";
export const fetchAutomation = createAsyncThunk(
  FETCH_AUTOMATION,
  async (workflowId: WorkflowAutomationId) => {
    const { workflow } = await oauthJson<{ workflow: WorkflowAutomationDetail }>(
      "GET",
      `workflow/${workflowId}`
    );
    return workflow;
  }
);

export const CREATE_AUTOMATION = "workflowAutomations/createAutomation";
export const createAutomation = createAsyncThunk<
  WorkflowAutomationDetail,
  WorkflowAutomationSaveInput,
  { rejectValue: string }
>(CREATE_AUTOMATION, async (workflow, { rejectWithValue }) => {
  try {
    const { workflow: created } = await oauthJson<{
      workflow: WorkflowAutomationDetail;
    }>("POST", "workflow", { workflow });
    return created;
  } catch (e: any) {
    return rejectWithValue(
      parseWorkflowErrorMessage(e, "Failed to create workflow automation")
    );
  }
});

export const SAVE_AUTOMATION = "workflowAutomations/saveAutomation";
export const saveAutomation = createAsyncThunk<
  WorkflowAutomationDetail,
  { workflowId: WorkflowAutomationId; workflow: WorkflowAutomationSaveInput },
  { rejectValue: string }
>(SAVE_AUTOMATION, async ({ workflowId, workflow }, { rejectWithValue }) => {
  try {
    const { workflow: saved } = await oauthJson<{
      workflow: WorkflowAutomationDetail;
    }>("PUT", `workflow/${workflowId}`, { workflow });
    return saved;
  } catch (e: any) {
    return rejectWithValue(
      parseWorkflowErrorMessage(e, "Failed to save workflow automation")
    );
  }
});

export const FETCH_CONFIGURATION_OPTIONS =
  "workflowAutomations/fetchConfigurationOptions";
export const fetchConfigurationOptions = createAsyncThunk(
  FETCH_CONFIGURATION_OPTIONS,
  async (modelType: WorkflowAutomationModelType | undefined) => {
    const raw = await oauthJson<WorkflowRawConfigurationOptions>(
      "GET",
      "workflow/configuration-options",
      modelType ? { model_type: modelType } : {}
    );
    return normalizeConfigurationOptions(raw);
  }
);

export const TOGGLE_AUTOMATION_ACTIVE =
  "workflowAutomations/toggleAutomationActive";
export const toggleAutomationActive = createAsyncThunk<
  WorkflowAutomationDetail,
  { workflowId: WorkflowAutomationId; active: boolean },
  { rejectValue: string }
>(TOGGLE_AUTOMATION_ACTIVE, async ({ workflowId, active }, { rejectWithValue }) => {
  try {
    // The update endpoint validates a full workflow payload, so the current
    // detail must be fetched before re-saving it with only `active` flipped.
    const { workflow } = await oauthJson<{ workflow: WorkflowAutomationDetail }>(
      "GET",
      `workflow/${workflowId}`
    );
    const saveInput = toWorkflowAutomationSaveInput(workflow, { active });
    const { workflow: saved } = await oauthJson<{
      workflow: WorkflowAutomationDetail;
    }>("PUT", `workflow/${workflowId}`, { workflow: saveInput });
    return saved;
  } catch (e: any) {
    return rejectWithValue(
      parseWorkflowErrorMessage(e, "Failed to update workflow automation status")
    );
  }
});

export const CREATE_PREDEFINED_AUTOMATIONS =
  "workflowAutomations/createPredefinedAutomations";
export const createPredefinedAutomations = createAsyncThunk<
  WorkflowAutomationDetail[],
  void,
  { rejectValue: string }
>(CREATE_PREDEFINED_AUTOMATIONS, async (_arg, { rejectWithValue }) => {
  try {
    return await oauthJson<WorkflowAutomationDetail[]>("POST", "workflow/predefined");
  } catch (e: any) {
    return rejectWithValue(
      parseWorkflowErrorMessage(e, "Failed to create predefined workflow automations")
    );
  }
});

/**
 * Bootstraps the automations list on first load: fetches the tenant's workflows,
 * and if there are none, provisions the predefined starter set and refetches so the
 * list reflects the canonical server state.
 */
export const INITIALIZE_AUTOMATIONS_LIST =
  "workflowAutomations/initializeAutomationsList";
export const initializeAutomationsList = createAsyncThunk(
  INITIALIZE_AUTOMATIONS_LIST,
  async (_arg: void, { dispatch }) => {
    const workflows = await dispatch(fetchAutomations()).unwrap();
    if (workflows.length > 0) {
      return;
    }

    try {
      await dispatch(createPredefinedAutomations()).unwrap();
    } catch {
      // createPredefinedAutomations already recorded the error and toast.
      return;
    }

    await dispatch(fetchAutomations()).unwrap();
  },
  {
    condition: (_arg, { getState }) =>
      (getState() as RootState).workflowAutomations.listLoading !== "pending",
  }
);

const slice = createSlice({
  name: "workflowAutomations",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentAutomation: (state) => {
      state.currentAutomation = null;
      state.draftAutomation = null;
    },
    /** Replaces the editor's draft with an edited copy of the workflow. */
    setDraftAutomation: (state, action: PayloadAction<WorkflowAutomationDetail>) => {
      state.draftAutomation = action.payload;
    },
    /** Discards in-progress edits, resetting the draft back to the saved server state. */
    discardDraftAutomation: (state) => {
      state.draftAutomation = state.currentAutomation;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAutomations
      .addCase(fetchAutomations.pending, (state) => {
        state.listLoading = "pending";
        state.error = null;
      })
      .addCase(fetchAutomations.fulfilled, (state, action) => {
        state.listLoading = "idle";
        state.automations = action.payload;
      })
      .addCase(fetchAutomations.rejected, (state, action) => {
        state.listLoading = "idle";
        state.error = action.error?.message || "Failed to load workflow automations";
        toast.error(state.error);
      })

      // fetchAutomation
      .addCase(fetchAutomation.pending, (state) => {
        state.detailLoading = "pending";
        state.error = null;
      })
      .addCase(fetchAutomation.fulfilled, (state, action) => {
        state.detailLoading = "idle";
        state.currentAutomation = action.payload;
        state.draftAutomation = action.payload;
      })
      .addCase(fetchAutomation.rejected, (state, action) => {
        state.detailLoading = "idle";
        state.error = action.error?.message || "Failed to load workflow automation";
        toast.error(state.error);
      })

      // createAutomation
      .addCase(createAutomation.pending, (state) => {
        state.saveLoading = "pending";
        state.error = null;
      })
      .addCase(createAutomation.fulfilled, (state, action) => {
        state.saveLoading = "idle";
        state.currentAutomation = action.payload;
        state.draftAutomation = action.payload;
        state.automations.push({
          workflow_id: action.payload.workflow_id,
          name: action.payload.name,
          model_type: action.payload.model_type,
          active: action.payload.active,
          days: 0,
          last_triggered_at: action.payload.last_triggered_at,
          updated_by: action.payload.updated_by,
          n8n_provisioned: action.payload.n8n_provisioned,
        });
      })
      .addCase(createAutomation.rejected, (state, action) => {
        state.saveLoading = "idle";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to create workflow automation";
        toast.error(state.error);
      })

      // saveAutomation
      .addCase(saveAutomation.pending, (state) => {
        state.saveLoading = "pending";
        state.error = null;
      })
      .addCase(saveAutomation.fulfilled, (state, action) => {
        state.saveLoading = "idle";
        state.currentAutomation = action.payload;
        state.draftAutomation = action.payload;
        patchAutomationListRow(state, action.payload);
        toast.success("Workflow configuration saved");
      })
      .addCase(saveAutomation.rejected, (state, action) => {
        state.saveLoading = "idle";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to save workflow automation";
        toast.error(state.error);
      })

      // fetchConfigurationOptions
      .addCase(fetchConfigurationOptions.pending, (state) => {
        state.configurationOptionsLoading = "pending";
        state.error = null;
      })
      .addCase(fetchConfigurationOptions.fulfilled, (state, action) => {
        state.configurationOptionsLoading = "idle";
        state.configurationOptions = action.payload;
      })
      .addCase(fetchConfigurationOptions.rejected, (state, action) => {
        state.configurationOptionsLoading = "idle";
        state.error =
          action.error?.message || "Failed to load workflow configuration options";
        toast.error(state.error);
      })

      // toggleAutomationActive
      .addCase(toggleAutomationActive.pending, (state, action) => {
        state.togglingWorkflowId = action.meta.arg.workflowId;
        state.error = null;
      })
      .addCase(toggleAutomationActive.fulfilled, (state, action) => {
        state.togglingWorkflowId = null;
        patchAutomationListRow(state, action.payload);
      })
      .addCase(toggleAutomationActive.rejected, (state, action) => {
        state.togglingWorkflowId = null;
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to update workflow automation status";
        toast.error(state.error);
      })

      // createPredefinedAutomations
      .addCase(createPredefinedAutomations.pending, (state) => {
        state.listLoading = "pending";
        state.error = null;
      })
      .addCase(createPredefinedAutomations.fulfilled, (state) => {
        // Intentionally leaves `automations` untouched: `initializeAutomationsList`
        // refetches the canonical list right after this resolves.
        state.listLoading = "idle";
      })
      .addCase(createPredefinedAutomations.rejected, (state, action) => {
        state.listLoading = "idle";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to create predefined workflow automations";
        toast.error(state.error);
      });
  },
});

export const { clearError, clearCurrentAutomation, setDraftAutomation, discardDraftAutomation } =
  slice.actions;

// Selectors
export const selectAutomations = (state: RootState) =>
  state.workflowAutomations.automations;

export const selectAutomationById = createSelector(
  [selectAutomations, (_state: RootState, workflowId: WorkflowAutomationId) => workflowId],
  (automations, workflowId) =>
    automations.find((a) => a.workflow_id === workflowId) ?? null
);

export const selectCurrentAutomation = (state: RootState) =>
  state.workflowAutomations.currentAutomation;

export const selectDraftAutomation = (state: RootState) =>
  state.workflowAutomations.draftAutomation;

export const selectIsWorkflowAutomationDirty = createSelector(
  [selectCurrentAutomation, selectDraftAutomation],
  (currentAutomation, draftAutomation) =>
    currentAutomation !== null &&
    draftAutomation !== null &&
    JSON.stringify(currentAutomation) !== JSON.stringify(draftAutomation)
);

export const selectListLoading = (state: RootState) =>
  state.workflowAutomations.listLoading === "pending";

export const selectDetailLoading = (state: RootState) =>
  state.workflowAutomations.detailLoading === "pending";

export const selectSaveLoading = (state: RootState) =>
  state.workflowAutomations.saveLoading === "pending";

export const selectConfigurationOptions = (state: RootState) =>
  state.workflowAutomations.configurationOptions;

export const selectConfigurationOptionsLoading = (state: RootState) =>
  state.workflowAutomations.configurationOptionsLoading === "pending";

export const selectTogglingWorkflowId = (state: RootState) =>
  state.workflowAutomations.togglingWorkflowId;

export const selectIsWorkflowToggling = (
  state: RootState,
  workflowId: WorkflowAutomationId
) => state.workflowAutomations.togglingWorkflowId === workflowId;

export const selectWorkflowAutomationsError = (state: RootState) =>
  state.workflowAutomations.error;

export default slice;
